/**
 * Parse search query to extract scope prefix and actual query
 *
 * Supported scopes:
 * - v  → vehicles
 * - k  → customers (klant)
 * - si → sales invoices
 * - pi → purchase invoices
 * - wo → work orders
 * - p  → parts
 */

import type { EntityType } from './SearchProvider';

export type SearchScope = EntityType | 'all';

export interface ParsedSearchQuery {
  scope: SearchScope;
  query: string;
  /** The original scope prefix if one was detected */
  scopePrefix?: string;
}

const SCOPE_PREFIXES: Record<string, EntityType> = {
  v: 'vehicle',
  k: 'customer',
  si: 'salesInvoice',
  pi: 'purchaseInvoice',
  wo: 'workOrder',
  p: 'part',
};

const SCOPE_PREFIX_LABELS: Record<string, string> = {
  v: 'Voertuigen',
  k: 'Klanten',
  si: 'Verkoopfacturen',
  pi: 'Inkoopfacturen',
  wo: 'Werkorders',
  p: 'Onderdelen',
};

/**
 * Parse a search query to extract scope and actual search text
 *
 * Examples:
 * - "v xx1" → { scope: 'vehicle', query: 'xx1', scopePrefix: 'v' }
 * - "wo 25" → { scope: 'workOrder', query: '25', scopePrefix: 'wo' }
 * - "p rem" → { scope: 'part', query: 'rem', scopePrefix: 'p' }
 * - "bmw" → { scope: 'all', query: 'bmw' }
 */
export function parseSearchQuery(input: string): ParsedSearchQuery {
  const trimmed = input.trim();

  if (!trimmed) {
    return { scope: 'all', query: '' };
  }

  // Check for scope prefix (must be followed by space)
  // Try longer prefixes first (si, pi, wo) before single-char ones
  const sortedPrefixes = Object.keys(SCOPE_PREFIXES).sort((a, b) => b.length - a.length);

  for (const prefix of sortedPrefixes) {
    const pattern = new RegExp(`^${prefix}\\s+(.*)$`, 'i');
    const match = trimmed.match(pattern);

    if (match && match[1]) {
      const entityType = SCOPE_PREFIXES[prefix];
      if (entityType) {
        return {
          scope: entityType,
          query: match[1].trim(),
          scopePrefix: prefix,
        };
      }
    }
  }

  // No scope prefix found
  return { scope: 'all', query: trimmed };
}

/**
 * Get the display label for a scope
 */
export function getScopeLabel(scope: SearchScope): string | null {
  if (scope === 'all') return null;

  // Find the prefix for this scope
  for (const [prefix, entityType] of Object.entries(SCOPE_PREFIXES)) {
    if (entityType === scope) {
      const label = SCOPE_PREFIX_LABELS[prefix];
      return label ?? null;
    }
  }

  return null;
}

/**
 * Get all available scope prefixes for display
 */
export function getAvailableScopes(): Array<{
  prefix: string;
  label: string;
  entityType: EntityType;
}> {
  return Object.entries(SCOPE_PREFIXES).map(([prefix, entityType]) => ({
    prefix,
    label: SCOPE_PREFIX_LABELS[prefix] ?? prefix,
    entityType,
  }));
}
